import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@repo/ui/sheet";
import { Activity, AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchValuationRunDetail, type ValuationRunDetail } from "@/lib/api";

interface RunDetailDrawerProps {
  runId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function StatusIcon({ status }: { status: string }) {
  if (status === "completed") {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  }
  if (status === "failed") {
    return <AlertTriangle className="h-4 w-4 text-destructive" />;
  }
  if (status === "running") {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }
  return <Activity className="h-4 w-4 text-muted-foreground" />;
}

export function RunDetailDrawer({ runId, open, onOpenChange }: RunDetailDrawerProps) {
  const [detail, setDetail] = useState<ValuationRunDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !runId) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    async function load(id: string) {
      try {
        setIsLoading(true);
        setError(null);
        const next = await fetchValuationRunDetail(id);
        if (!cancelled) setDetail(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load run detail");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load(runId);
    return () => {
      cancelled = true;
    };
  }, [open, runId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Run detail</SheetTitle>
          <SheetDescription>
            Persisted observability for this run: step timeline, data source contribution, and per-month
            results.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading run detail...
            </p>
          ) : null}

          {error ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Failed to load</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          {detail ? (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Run summary</CardTitle>
                  <CardDescription>
                    {detail.run.suburb_name}, {detail.run.state} {detail.run.postcode}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="font-medium">{detail.run.status}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Started</p>
                    <p className="font-medium">{formatDateTime(detail.run.started_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Completed</p>
                    <p className="font-medium">{formatDateTime(detail.run.completed_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Months</p>
                    <p className="font-medium">{detail.run.months}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Step timeline</CardTitle>
                  <CardDescription>
                    Each pipeline stage: source selection → input filtering → dedupe → IQR outlier removal →
                    monthly aggregation → profile cross-check.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {detail.steps.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No step rows recorded for this run.
                    </p>
                  ) : null}
                  {detail.steps.map((step) => (
                    <div key={step.id} className="rounded-md border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <StatusIcon status={step.status} />
                          <p className="text-sm font-medium">{step.step_label}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px]">
                          {step.step_slug}
                        </Badge>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        {typeof step.input_count === "number" ? (
                          <div>
                            <span className="font-medium text-foreground">{step.input_count}</span> in
                          </div>
                        ) : null}
                        {typeof step.output_count === "number" ? (
                          <div>
                            <span className="font-medium text-foreground">{step.output_count}</span> out
                          </div>
                        ) : null}
                      </div>
                      {Object.keys(step.result_summary).length > 0 ? (
                        <pre className="mt-2 max-h-32 overflow-auto rounded bg-muted/40 p-2 text-[10px] leading-relaxed">
                          {JSON.stringify(step.result_summary, null, 2)}
                        </pre>
                      ) : null}
                      {step.warnings.length > 0 ? (
                        <ul className="mt-2 space-y-1 text-xs text-amber-700">
                          {step.warnings.map((warning, index) => (
                            <li key={`${step.id}-warning-${index}`}>{String(warning)}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Data source contribution</CardTitle>
                  <CardDescription>
                    Per-source record count + selection JSON captured at run time.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {detail.sources.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No source contribution recorded.
                    </p>
                  ) : null}
                  {detail.sources.map((source) => (
                    <div key={source.id} className="rounded-md border p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">
                            {source.valuation_data_sources?.name ?? source.source_id}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {source.valuation_data_sources?.slug ?? "unknown slug"} ·{" "}
                            {source.snapshot_kind}
                          </p>
                        </div>
                        <Badge variant="default">{source.record_count.toLocaleString()} rows</Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>

        <SheetClose asChild>
          <Button
            type="button"
            variant="ghost"
            className="absolute right-4 top-4 h-8 w-8 p-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
}
